import React, { FC, memo, useCallback, useMemo, useState } from "react";

// components
import { ErrorBoundary } from "@swiggy-private/core-ui";
import CartUpsellNudge from "./Components/CartUpsellNudge/CartUpsellNudge";
import { OneBlckPlanRemoveConfirmationBottomsheet } from "@swiggy-private/dls-web-recipe-common-web/dist/components/SwiggyOne";
import { GratificationPopup } from "../GratificationPopup";

// types
import { OneMemberShipNudgeProps } from "./types";

// utils
import { getNRErrorInfo, isEmpty } from "@swiggy-private/js-utils";
import { isOneLite, isSwiggyBlack } from "@super-ui/components/swiggy-one-widget-dls-wrapper/helpers";

// hooks
import { useGetRemoveBSProps } from "@super-ui/components/cart-upsell-nudge/hooks/useGetRemoveBSProps";
import { PlanRemoveConfirmationBottomsheetWrapper } from "./Components/PlanRemoveConfirmationBottomsheetWrapper";
import { NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";

const OneMembershipNudge: FC<OneMemberShipNudgeProps> = props => {
    const {
        itemNudgeComms,
        planDetails,
        gratificationComms,
        removePlanMessage,
        handlers,
        config,
        analyticsEvents,
        isCartV2,
    } = props;
    const { currentSid, sessionKey, isAnyModalVisible } = config;
    const { nudgeAnalytics, gratificationAnalytics } = analyticsEvents;

    const [showRemovePlanConfirmation, setShowRemovePlanConfirmation] = useState(false);

    const { bottomSheetProps, allowPlanRemovalBottomsheet } = useGetRemoveBSProps({
        removePlanMessage,
        handlers: { ...handlers, setShowRemovePlanConfirmation, nudgeAnalytics },
        planDetails,
        showRemovePlanConfirmation,
    });
    const isOneLitePlan = useMemo(() => isOneLite(planDetails), [planDetails]);
    const isSwiggyBlackPlan = useMemo(() => isSwiggyBlack(planDetails), [planDetails]);

    // Prepare nudge communications data
    const nudgeComms = useMemo(() => {
        if (isEmpty(itemNudgeComms) || isEmpty(planDetails)) return null;
        return {
            ...itemNudgeComms,
            logoCtx: itemNudgeComms.logo_ctx,
            attributeValues: itemNudgeComms.attribute_values,
            planDetails,
            handlers,
            nudgeAnalytics,
            setShowRemovePlanConfirmation,
            isCartV2,
            isBlackPlan: isSwiggyBlackPlan,
            removePlanMessage,
        };
    }, [handlers, itemNudgeComms, nudgeAnalytics, planDetails, isCartV2, isSwiggyBlackPlan, removePlanMessage]);

    // Prepare gratification data
    const gratificationData = useMemo(() => {
        if (isEmpty(gratificationComms)) return null;
        return {
            sessionKey,
            currentSid,
            isOneLite: isOneLitePlan,
            isSwiggyBlack: isSwiggyBlackPlan,
            gratificationComms,
            isAnyModalVisible,
            analyticsEvents: gratificationAnalytics,
        };
    }, [
        currentSid,
        gratificationAnalytics,
        gratificationComms,
        isAnyModalVisible,
        isOneLitePlan,
        isSwiggyBlackPlan,
        sessionKey,
    ]);

    const PlanRemoveBottomsheet = useMemo(
        () =>
            isSwiggyBlackPlan ? (
                <OneBlckPlanRemoveConfirmationBottomsheet {...bottomSheetProps} />
            ) : (
                <PlanRemoveConfirmationBottomsheetWrapper
                    removePlanMessage={removePlanMessage}
                    bottomSheetProps={bottomSheetProps}
                    planDetails={planDetails}
                />
            ),
        [bottomSheetProps, isSwiggyBlackPlan, removePlanMessage, planDetails],
    );

    const onError = useCallback(
        (error?: Error, errorInfo?: React.ErrorInfo) => {
            const errorEvents = getNRErrorInfo(error, errorInfo);

            recordNREvent({
                eventType: NEWRELIC_TABLES.CHECKOUT_WEB_ERROR_BOUNDARY,
                widgetType: "cart-upsell-nudge",
                ...errorEvents,
                nudgeComms: JSON.stringify(nudgeComms),
            });
        },
        [nudgeComms],
    );

    return (
        <ErrorBoundary errorContent={null} errorFallback={onError}>
            {nudgeComms && <CartUpsellNudge {...nudgeComms} />}
            {gratificationData && <GratificationPopup {...gratificationData} />}
            {allowPlanRemovalBottomsheet && PlanRemoveBottomsheet}
        </ErrorBoundary>
    );
};

export default memo(OneMembershipNudge);
